import { useEffect, useRef } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';

/**
 * ApartmentSync emits "View Details" as a relative href
 * (`floor-plans/layout?floorplan=…`). From `/floor-plans` the browser
 * resolves that to `/floor-plans/floor-plans/layout`, which this site
 * serves as the home page. Root-absolute `/floor-plans/layout` is the
 * Floor Plan Details page.
 */
const DETAILS_PATH = '/floor-plans/layout';

function toAbsoluteFloorplanDetailsHref(href: string): string | null {
  const raw = href.trim();
  if (!raw || /^(?:#|javascript:|mailto:|tel:)/i.test(raw)) return null;
  if (raw.startsWith(DETAILS_PATH)) return null;

  const relative = raw.replace(/^\.\//, '');
  const match = relative.match(/^floor-plans\/layout\/?(\?[^#]*)?(#.*)?$/);
  if (!match) return null;

  return `${DETAILS_PATH}${match[1] || ''}${match[2] || ''}`;
}

function rewriteFloorplanDetailsLinks(root: ParentNode) {
  root.querySelectorAll<HTMLAnchorElement>('a[href*="floor-plans/layout"]').forEach((anchor) => {
    const next = toAbsoluteFloorplanDetailsHref(anchor.getAttribute('href') || '');
    if (next) anchor.setAttribute('href', next);
  });
}

/**
 * Thin wrapper around the `apartment-sync` block. The wrapped block is
 * swappable via the editor (constrained to the Integrations category).
 */
export function ApartmentSyncSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const blockSlot = useBlockSlot('apartmentsyncsection.block', {
    defaultValue: 'apartment-sync',
  });

  useEffect(() => {
    const root = sectionRef.current;
    if (!root) return;

    rewriteFloorplanDetailsLinks(root);
    const observer = new MutationObserver(() => rewriteFloorplanDetailsLinks(root));
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href'],
    });

    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest('a');
      if (!anchor || !root.contains(anchor)) return;
      const next = toAbsoluteFloorplanDetailsHref(anchor.getAttribute('href') || '');
      if (!next) return;
      anchor.setAttribute('href', next);
      event.preventDefault();
      if (anchor.getAttribute('target') === '_blank') {
        window.open(next, '_blank', 'noopener');
        return;
      }
      window.location.assign(next);
    };

    root.addEventListener('click', onClick, true);
    return () => {
      observer.disconnect();
      root.removeEventListener('click', onClick, true);
    };
  }, []);

  return (
    <section ref={sectionRef} className="apartment-sync-section bg-background">
      <EditableToken path="apartmentsyncsection.block" as="div" className="apartment-sync-section__inner">
        {blockSlot ? (
          <SectionContextProvider sectionId={blockSlot.sectionId}>
            <blockSlot.BlockComponent sectionId={blockSlot.sectionId} />
          </SectionContextProvider>
        ) : (
          <div className="apartment-sync-empty">No block selected</div>
        )}
      </EditableToken>

      <style>{`
        .apartment-sync-section {
          width: 100%;
          min-height: 400px;
        }
        .apartment-sync-section__inner {
          min-height: 400px;
        }
        .apartment-sync-empty {
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text-muted);
          text-align: center;
          padding: 2rem 1rem;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ApartmentSyncSection',
  displayName: 'Apartment Sync Section',
  tokens: {
    'apartmentsyncsection.block': {
      label: 'Block',
      type: 'block',
      defaultValue: 'apartment-sync',
      acceptedBlockCategories: ['Integrations'],
    },
  },
};

(ApartmentSyncSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ApartmentSyncSection;
